import { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  target?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface OnboardingTourProps {
  onComplete: () => void;
  onSkip: () => void;
}

const tourSteps: TourStep[] = [
  {
    title: 'Welcome to Mentra!',
    description: 'Let\'s take a quick tour to help you get started with collecting and analyzing feedback. This will only take a minute.'
  },
  {
    title: 'Request Feedback',
    description: 'Click the "Request Feedback" button to send personalized feedback requests to colleagues, mentors, or team members via email.',
    target: 'request-feedback-btn'
  },
  {
    title: 'Track Your Requests',
    description: 'View all your feedback requests here. See which are pending, completed, or expired. You can also copy shareable links.',
    target: 'requests-tab'
  },
  {
    title: 'AI-Powered Insights',
    description: 'Once you receive feedback, click here to view AI-generated insights about your strengths, themes, and development areas.',
    target: 'insights-tab'
  },
  {
    title: 'Analytics Dashboard',
    description: 'Track your feedback trends over time, see response rates, and analyze sentiment patterns with beautiful visualizations.',
    target: 'analytics-tab'
  },
  {
    title: 'Usage & Upgrades',
    description: 'Monitor your monthly usage here. Your current plan limits are displayed, and you can upgrade anytime for more features.',
    target: 'usage-info'
  },
  {
    title: 'You\'re All Set!',
    description: 'That\'s it! Start by requesting your first feedback. Remember, the more feedback you collect, the better your AI insights will be.'
  }
];

export default function OnboardingTour({ onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Remove any highlighted elements
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
    setIsVisible(false);
    setTimeout(onComplete, 300);
  };

  const handleSkipTour = () => {
    // Remove any highlighted elements
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
    setIsVisible(false);
    setTimeout(onSkip, 300);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkipTour();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Highlight the target element
  useEffect(() => {
    const step = tourSteps[currentStep];
    if (!step.target) return;

    const targetElement = document.getElementById(step.target);
    if (!targetElement) return;

    // Add highlight classes
    targetElement.classList.add('tour-highlight');

    // Scroll element into view
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Create and inject styles if not already present
    if (!document.getElementById('tour-highlight-styles')) {
      const style = document.createElement('style');
      style.id = 'tour-highlight-styles';
      style.textContent = `
        .tour-highlight {
          position: relative;
          z-index: 45 !important;
          animation: tour-pulse 2s ease-in-out infinite;
        }
        .tour-highlight::before {
          content: '';
          position: absolute;
          inset: -4px;
          border: 3px solid #8CAFA8;
          border-radius: 12px;
          pointer-events: none;
          animation: tour-glow 2s ease-in-out infinite;
        }
        @keyframes tour-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(140, 175, 168, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(140, 175, 168, 0);
          }
        }
        @keyframes tour-glow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Cleanup function
    return () => {
      targetElement.classList.remove('tour-highlight');
    };
  }, [currentStep]);

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-[#E0EBE9] text-[#8CAFA8] text-sm font-semibold px-3 py-1 rounded-full">
                    Step {currentStep + 1} of {tourSteps.length}
                  </div>
                  {isLastStep && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-semibold">Almost done!</span>
                    </div>
                  )}
                </div>
                <h2 className="text-2xl font-semibold text-[#1F2937]">{step.title}</h2>
              </div>
              <button
                onClick={handleSkipTour}
                className="text-[#D1D5DB] hover:text-[#6B7280] transition p-1"
                aria-label="Close tour"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="bg-[#8CAFA8] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="p-6">
            <p className="text-[#4B5563] text-lg leading-relaxed mb-6">
              {step.description}
            </p>

            {step.target && (
              <div className="bg-[#F0F4F3] border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">💡 Look for:</span> This element is highlighted in the dashboard
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              {!isFirstStep && (
                <button
                  onClick={handlePrevious}
                  className="flex items-center gap-2 px-5 py-2.5 text-[#4B5563] border border-gray-200 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>
              )}

              {isFirstStep && (
                <button
                  onClick={handleSkipTour}
                  className="px-5 py-2.5 text-[#6B7280] hover:text-[#1F2937] transition font-medium"
                >
                  Skip Tour
                </button>
              )}

              <div className="flex-1" />

              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#8CAFA8] text-white rounded-lg hover:bg-[#7A9B94] transition font-semibold shadow-[0_2px_12px_rgba(0,0,0,0.04)] shadow-[#8CAFA8]/30"
              >
                {isLastStep ? (
                  <>
                    <Check className="w-5 h-5" />
                    Get Started
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-2">
              {tourSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-[#8CAFA8]'
                      : index < currentStep
                      ? 'w-2 bg-blue-400'
                      : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
